import { useEffect, useState } from 'react';
import { keyBy, noop } from 'lodash';
import { Switch } from '@material-ui/core';
import { useSelector } from 'react-redux';
import AppRouter from 'common/AppRouter';
import { useHistory } from 'react-router';
import { Stack } from '@mui/material';
import {
  FEATURE_FLAGS_NAMES,
  CAN_EDIT_ACCOUNT_TYPES
} from '@linearb/linjs-common';
import {
  Tooltip,
  FilterIcon,
  TagListFilter,
  WarningBox,
  lightColorPalette
} from '@linearb/linui-common';
import { H4, Body1 } from '@linearb/linui-common/typography';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { repositoriesSelector } from 'state/selectors/repositories';
import { getServicesSelector } from 'state/selectors/services';
import { myAccountSelector } from 'state/selectors/account';
import { isTeam } from 'common/utils/utils';
import useSettingsService from 'components/hooks/useSettingsService';
import useGitTeamSettings from 'views/root/views/home/views/settings/views/GitSettings/GitTeamSettings/useGitTeamSettings';
import {
  getIsFilterByGitScope,
  setFilterByGitScope,
  mapViewPathToSessionKey
} from './utils';
import { LBButton } from '@linearb/buzz';

const buildTagsList = ({ tags, label = '' }) => {
  return tags?.length
    ? {
        and: [
          {
            label,
            value: label.toLowerCase(),
            or: tags
          }
        ]
      }
    : null;
};

const TeamGitScopeTooltip = ({
  selectedTeam,
  reposIds = [],
  servicesIds = [],
  sessionKey,
  gitScopeToggle,
  setGitScopeToggle,
  toggleOn,
  setToggleOn,
  isTopBarView = false
}) => {
  useEffect(() => {
    setFilterByGitScope(sessionKey, toggleOn);
  }, [toggleOn]);

  const history = useHistory();
  const repositories = useSelector(repositoriesSelector);
  const services = useSelector(getServicesSelector);
  const account = useSelector(myAccountSelector);
  const canEdit = CAN_EDIT_ACCOUNT_TYPES.includes(account?.type_id);

  const repositoriesById = keyBy(repositories, 'id');
  const servicesById = keyBy(services, 'id');

  const repositoriesTags = reposIds.map(id => {
    return { label: repositoriesById?.[id]?.name, value: id };
  });
  const repositoriesTagList = buildTagsList({
    tags: repositoriesTags,
    label: 'Repositories'
  });

  const servicesTags = servicesIds.map(id => {
    return { label: servicesById?.[id]?.name, value: id };
  });
  const servicesTagList = buildTagsList({
    tags: servicesTags,
    label: 'Services'
  });

  return (
    <Stack gap='0.5rem' sx={{ padding: '0.5rem 0 1rem 0' }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <H4 color={lightColorPalette.lbGreyDark} bold>
          {selectedTeam?.name} Git Scope
        </H4>
        {isTopBarView && (
          <Stack direction='row' gap='0.5rem'>
            <Stack direction='row' alignItems='center'>
              <Switch
                size='small'
                checked={toggleOn}
                onChange={event => {
                  setToggleOn(event.target.checked);
                  setGitScopeToggle({
                    ...gitScopeToggle,
                    [sessionKey]: event.target.checked
                  });
                }}
              />
              <Body1 color={lightColorPalette.lbGreyDark}>
                Filter: {toggleOn ? 'on' : 'off'}
              </Body1>
            </Stack>
            {canEdit && (
              <LBButton
                variant='ghost'
                onClick={() => history.push(AppRouter.REPOSITORIES.path)}
              >
                Edit
              </LBButton>
            )}
          </Stack>
        )}
      </Stack>

      <Body1 color={lightColorPalette.lbGreyDark}>
        This view is filtered by the following scope:
      </Body1>
      {repositoriesTagList && (
        <TagListFilter className='reposTagList' value={repositoriesTagList} />
      )}
      {repositoriesTagList && servicesTagList && (
        <Body1
          color={lightColorPalette.lbGreyDark}
          bold
          data-testid='repos-or-services'
        >
          OR
        </Body1>
      )}
      {servicesTagList && (
        <TagListFilter className='servicesTagList' value={servicesTagList} />
      )}
    </Stack>
  );
};

const TeamGitScopeBanner = ({
  team,
  viewPath,
  gitScopeToggle,
  setGitScopeToggle = noop
}) => {
  const sessionKey = mapViewPathToSessionKey(viewPath);

  const [toggleOn, setToggleOn] = useState(null);

  const { getSettingOrDefinitionValue } = useSettingsService();
  const isTeamGitSettingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamGitSettingEnabled
  });
  const globallySelectedTeam = useSelector(selectedTeamSelector);
  const selectedTeam = team ? team : globallySelectedTeam;
  const gitTeamSettings = useGitTeamSettings({ teamId: selectedTeam?.id });
  const { reposIds = [], servicesIds = [] } = gitTeamSettings;

  const isTeamWithGitScope =
    isTeamGitSettingEnabled &&
    isTeam(selectedTeam) &&
    (reposIds?.length > 0 || servicesIds?.length > 0);

  useEffect(() => {
    if (isTeamWithGitScope) {
      const isGitScopeFiltered = getIsFilterByGitScope(sessionKey);
      setToggleOn(isGitScopeFiltered);
      setGitScopeToggle({
        ...gitScopeToggle,
        [sessionKey]: isGitScopeFiltered
      });
    } else {
      setGitScopeToggle(null);
    }
  }, [viewPath, selectedTeam?.id]);

  if (!isTeamWithGitScope) {
    return null;
  }

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '80rem',
            '.reposTagList .MuiStack-root': { display: 'inline' },
            '.servicesTagList .MuiStack-root': { display: 'inline' },
            '.MuiChip-deleteIcon': { display: 'none' } // Fix in linui-common
          }
        }
      }}
      title={
        <TeamGitScopeTooltip
          selectedTeam={selectedTeam}
          reposIds={reposIds}
          servicesIds={servicesIds}
          sessionKey={sessionKey}
          gitScopeToggle={gitScopeToggle}
          setGitScopeToggle={setGitScopeToggle}
          toggleOn={toggleOn}
          setToggleOn={setToggleOn}
          isTopBarView={!team}
        />
      }
      placement='bottom-start'
    >
      {team ? (
        <WarningBox
          sx={{
            padding: '7px 11px',
            '.MuiAlert-icon': {
              display: 'none'
            },
            '.MuiAlert-message': {
              padding: '0'
            },
            marginLeft: '1rem'
          }}
        >
          <FilterIcon />
        </WarningBox>
      ) : (
        <WarningBox
          sx={{
            marginRight: '1rem',
            whiteSpace: 'nowrap',
            '.MuiAlert-message': {
              color: toggleOn
                ? lightColorPalette.lbGreyDark
                : lightColorPalette.lbGreyDisabled
            }
          }}
        >
          Filtered View
        </WarningBox>
      )}
    </Tooltip>
  );
};

export default TeamGitScopeBanner;
